import { purchaseBookingLink } from "./purchaseBookingLink.js";
import { ActionConfirmation } from "./ui/ActionConfirmation.js";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "./auth.js";
import {
  creditReviewApi,
  type CancellationCreditResolution,
  type CreditReviewItem,
  type CreditReviewScope,
} from "./creditReview.js";
import { Pagination } from "./Pagination.js";
import { PurchaseClaimQueue } from "./PurchaseClaimQueue.js";
import { creditReviewGuidance } from "./creditReviewGuidance.js";
import { Dialog } from "./ui/Dialog.js";
import { useAsync } from "./useAsync.js";
import { CARD_RECORD_PAGE_SIZE } from "./paginationPageSizes.js";

const PAGE_SIZE = CARD_RECORD_PAGE_SIZE;

function readPage(value: string | null): number {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : 1;
}

function reviewKindLabel(kind: string): string {
  if (kind === "cancellation_requires_attention") return "Cancellation credit";
  if (kind === "cancellation_redecision_required") return "Cancelled again";
  if (kind === "paid_cancellation_credit_requires_attention") return "Paid cancellation credit";
  if (kind === "cancellation_funding_requires_review") return "Cancelled lesson payment";
  if (kind === "coupon_restoration_requires_attention") return "Coupon use not given back";
  if (kind === "coupon_capacity_requires_attention") return "Website coupon not updated";
  if (kind === "coupon_conversion_requires_review") return "Coupon conversion held";
  if (kind === "coupon_redeemed_on_inactive_block") return "Coupon used after refund";
  if (kind === "coupon_expiry_requires_attention") return "Website coupon expiry";
  if (kind === "paid_cancellation_payment_reversed") return "Rebooking credit's payment reversed";
  if (kind === "paid_cancellation_lesson_reinstated") return "Cancelled lesson booked again";
  return kind
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function cancellationSummary(item: CreditReviewItem): string | null {
  if (item.kind !== "cancellation_credit_decision_required") return null;
  const reason = item.detail["reason"];
  if (reason === "appointment_deleted") return "Deleted from Amelia";
  const notice = item.detail["observed_notice_hours"];
  if (typeof notice === "number" && Number.isFinite(notice)) {
    if (notice >= 1) {
      const hours = Math.floor(notice);
      return `Cancelled in Amelia at least ${hours} ${hours === 1 ? "hour" : "hours"} before the lesson`;
    }
    if (notice >= 1 / 60) {
      const minutes = Math.floor(notice * 60);
      return `Cancelled in Amelia at least ${minutes} ${minutes === 1 ? "minute" : "minutes"} before the lesson`;
    }
    if (notice > 0) return "Cancelled in Amelia shortly before the lesson";
    return "Cancelled in Amelia; the notice given isn't known because it was first noticed after the lesson started";
  }
  return "Cancelled in Amelia; the notice given isn't known";
}

function contextText(item: CreditReviewItem): string {
  const parts: string[] = [];
  if (item.student_name) parts.push(item.student_name);
  if (item.lesson_starts_at) {
    const startsAt = new Date(item.lesson_starts_at);
    const text = Number.isNaN(startsAt.valueOf())
      ? item.lesson_starts_at
      : new Intl.DateTimeFormat("en-GB", {
          weekday: "short",
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(startsAt);
    parts.push(`Lesson ${text}`);
  }
  return parts.join(" · ");
}

function observedText(item: CreditReviewItem): string {
  const first = new Date(item.first_seen_at);
  const last = new Date(item.last_seen_at);
  const firstText = Number.isNaN(first.valueOf()) ? item.first_seen_at : first.toLocaleString();
  const lastText = Number.isNaN(last.valueOf()) ? item.last_seen_at : last.toLocaleString();
  return firstText === lastText
    ? `Seen ${lastText}`
    : `First seen ${firstText} · last seen ${lastText}`;
}

export function CreditReviewQueue({ onChanged }: { onChanged: () => void }) {
  const { me } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = readPage(searchParams.get("page"));
  const scope: CreditReviewScope = useMemo(
    () =>
      me?.capabilities.includes("lesson_blocks.manage.all")
        ? "organisation"
        : "mine",
    [me?.capabilities],
  );
  const loaded = useAsync(
    () => creditReviewApi.list(scope, page, PAGE_SIZE),
    [scope, page],
  );
  const [dismissing, setDismissing] = useState<CreditReviewItem | null>(null);
  const [note, setNote] = useState("");
  const [dismissAttempt, setDismissAttempt] = useState<{
    itemId: string;
    note: string;
    key: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [decision, setDecision] = useState<{
    item: CreditReviewItem;
    resolution: CancellationCreditResolution;
    key: string;
  } | null>(null);
  const [decisionResult, setDecisionResult] = useState<string | null>(null);
  const [repairing, setRepairing] = useState<CreditReviewItem | null>(null);
  const [clearing, setClearing] = useState<CreditReviewItem | null>(null);
  const [clearReason, setClearReason] = useState("");

  const items = loaded.data?.items ?? [];
  const pages = Math.max(1, Math.ceil((loaded.data?.total ?? items.length) / PAGE_SIZE));

  function choosePage(nextPage: number) {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete("page");
    else next.set("page", String(nextPage));
    setSearchParams(next);
  }

  useEffect(() => {
    if (!loaded.loading && page > pages) choosePage(pages);
  }, [loaded.loading, page, pages]);

  function openDismiss(item: CreditReviewItem) {
    setDismissing(item);
    setNote("");
    setDismissAttempt(null);
    setActionError(null);
  }

  function closeDismiss() {
    if (busy) return;
    setDismissing(null);
    setNote("");
    setDismissAttempt(null);
    setActionError(null);
  }

  function openDecision(
    item: CreditReviewItem,
    resolution: CancellationCreditResolution,
  ) {
    setDecision({ item, resolution, key: crypto.randomUUID() });
    setActionError(null);
  }

  function closeDecision() {
    if (busy) return;
    setDecision(null);
    setActionError(null);
  }

  async function confirmDecision() {
    if (!decision || busy || decision.item.amelia_appointment_id === null) return;
    setBusy(true);
    setActionError(null);
    try {
      const result = await creditReviewApi.decideCancellation(
        decision.item.amelia_appointment_id,
        decision.resolution,
        decision.key,
      );
      setDecisionResult(
        result.credit_review_required
          ? "Credit needs review"
          : result.credit_resolution === "restore"
            ? "Credit returned"
            : "Credit kept",
      );
      setDecision(null);
      loaded.reload();
      onChanged();
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Could not record this credit decision.",
      );
    } finally {
      setBusy(false);
    }
  }

  function openRepair(item: CreditReviewItem) {
    setRepairing(item);
    setActionError(null);
  }

  function closeRepair() {
    if (busy) return;
    setRepairing(null);
    setActionError(null);
  }

  async function confirmRepair() {
    if (!repairing || busy || repairing.amelia_appointment_id === null) return;
    setBusy(true);
    setActionError(null);
    try {
      await creditReviewApi.repairPaidCancellationCredit(repairing.amelia_appointment_id);
      setDecisionResult("Rebooking credit issued");
      setRepairing(null);
      loaded.reload();
      onChanged();
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Could not issue this rebooking credit.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function confirmClear() {
    if (!clearing?.lesson_block_id || clearReason.trim().length < 3 || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await creditReviewApi.clearPaymentReversal(clearing.lesson_block_id, clearReason.trim());
      setDecisionResult("Payment hold cleared after WMF verified the original payment. Refresh reviews to confirm reconciliation.");
      setClearing(null);
      setClearReason("");
      loaded.reload();
      onChanged();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Could not clear the payment hold.");
    } finally {
      setBusy(false);
    }
  }

  async function confirmDismiss() {
    const trimmedNote = note.trim();
    if (!dismissing || busy) return;

    const attempt =
      dismissAttempt?.itemId === dismissing.id && dismissAttempt.note === trimmedNote
        ? dismissAttempt
        : {
            itemId: dismissing.id,
            note: trimmedNote,
            key: crypto.randomUUID(),
          };
    if (attempt !== dismissAttempt) setDismissAttempt(attempt);

    setBusy(true);
    setActionError(null);
    try {
      await creditReviewApi.dismiss(dismissing.id, trimmedNote, attempt.key);
      setDismissing(null);
      setNote("");
      setDismissAttempt(null);
      loaded.reload();
      onChanged();
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Could not dismiss this review item.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h2>Credits</h2>
      <p className="small muted operations-queue-description">
        Financial exceptions that need a person to review them. Marking Action
        Centre items reviewed only clears the attention badge; it does not
        resolve or dismiss these records.
      </p>
      {scope === "organisation" && <PurchaseClaimQueue onChanged={onChanged} />}
      {decisionResult && <ActionConfirmation variant={decisionResult === "Credit needs review" ? "attention" : "success"} title="Credit decision recorded" description={decisionResult} reveal />}
      <div id="credit-review-items" className="panel operations-queue-panel">
        {loaded.loading && <p className="empty">Loading credit reviews…</p>}
        {loaded.error && <div className="error">{loaded.error}</div>}
        {!loaded.loading && !loaded.error && items.length === 0 && (
          <p className="empty">No credit reviews need attention.</p>
        )}
        {items.map((item) => {
          const context = contextText(item);
          const cancellation = cancellationSummary(item);
          const legacyHeld = item.kind === "credit_history_inconsistent" && item.detail["reason"] === "held_redemption_authority" && item.detail["redemption_authority"] === "migration_hold";
          const guidance = creditReviewGuidance(legacyHeld ? "legacy_credit_hold" : item.kind);
          const bookingLink = item.kind === "purchase_student_unresolved"
            ? purchaseBookingLink(item.amelia_booking_id, item.amelia_appointment_id) : null;
          const canDismiss = item.available_actions.includes("dismiss");
          const canReturn = item.available_actions.includes("return_credit");
          const canKeep = item.available_actions.includes("keep_credit");
          const canRepair =
            item.amelia_appointment_id !== null &&
            item.available_actions.includes("repair_paid_cancellation_credit");
          return (
            <div className="slot operations-row" key={item.id}>
              <div style={{ flex: 1 }}>
                <div className="row" style={{ justifyContent: "space-between", gap: 8 }}>
                  <strong>{legacyHeld ? "Legacy credits awaiting review" : reviewKindLabel(item.kind)}</strong>
                  <span className={item.severity === "action" ? "tag warn" : "tag"}>
                    {item.severity === "action" ? "Needs attention" : "Review"}
                  </span>
                </div>
                <div>{cancellation ?? item.message}</div>
                <div className="small"><strong>Money:</strong> {guidance.money}</div>
                <div className="small"><strong>Credits:</strong> {guidance.spendability}</div>
                <div className="small"><strong>Next:</strong> {guidance.next}</div>
                {bookingLink && <div className="small"><Link to={bookingLink}>Match original purchase booking</Link></div>}
                {item.portal_student_id && <div className="small"><Link to={`/students/${encodeURIComponent(item.portal_student_id)}`}>Open student</Link></div>}
                {legacyHeld && scope === "organisation" && item.portal_student_id && item.lesson_block_id && <div className="small"><Link to={`/students/${encodeURIComponent(item.portal_student_id)}?tab=admin#credit-block-${encodeURIComponent(item.lesson_block_id)}`}>Review legacy credits</Link></div>}
                {context && <div className="small muted">{context}</div>}
                <div className="small muted">{observedText(item)}</div>
              </div>
              {(canReturn || canKeep || canRepair || canDismiss || (scope === "organisation" && item.kind === "purchase_payment_reversed" && item.lesson_block_id !== null)) && (
                <div className="row operations-row-actions">
                  {scope === "organisation" && item.kind === "purchase_payment_reversed" && item.lesson_block_id && <button type="button" className="quiet" onClick={() => { setClearing(item); setClearReason(""); setActionError(null); }}>Verify paid and clear hold</button>}
                  {canRepair && (
                    <button type="button" className="quiet" onClick={() => openRepair(item)}>
                      Issue rebooking credit
                    </button>
                  )}
                  {canReturn && (
                    <button
                      type="button"
                      className="quiet"
                      onClick={() => openDecision(item, "restore")}
                    >
                      Return credit
                    </button>
                  )}
                  {canKeep && (
                    <button
                      type="button"
                      className="quiet"
                      onClick={() => openDecision(item, "consume")}
                    >
                      Keep credit used
                    </button>
                  )}
                  {canDismiss && (
                    <button type="button" className="quiet" onClick={() => openDismiss(item)}>
                      Dismiss
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Pagination
        page={page}
        pages={pages}
        disabled={loaded.loading}
        onPageChange={choosePage}
        ariaLabel="Credit review pages"
      />

      {decision && (
        <Dialog
          title={decision.resolution === "restore" ? "Return this lesson credit?" : "Keep this credit used?"}
          description={
            decision.resolution === "restore"
              ? "This records the Amelia cancellation decision and returns the credit if the block can receive it."
              : "This records that the cancelled lesson should keep the credit already used."
          }
          onClose={closeDecision}
          size="small"
          busy={busy}
        >
          {actionError && <div className="error">{actionError}</div>}
          <div className="ui-confirm-actions">
            <button
              type="button"
              className="action-secondary"
              onClick={closeDecision}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="action-primary"
              onClick={() => void confirmDecision()}
              disabled={busy}
            >
              {busy
                ? "Saving…"
                : decision.resolution === "restore"
                  ? "Return credit"
                  : "Keep credit used"}
            </button>
          </div>
        </Dialog>
      )}

      {repairing && (
        <Dialog
          title="Issue this rebooking credit?"
          description="The lesson was already paid for and has been cancelled. This adds the one rebooking credit that cancellation was due. No refund is made and no new payment is recorded."
          onClose={closeRepair}
          size="small"
          busy={busy}
        >
          {actionError && <div className="error">{actionError}</div>}
          <div className="ui-confirm-actions">
            <button
              type="button"
              className="action-secondary"
              onClick={closeRepair}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="action-primary"
              onClick={() => void confirmRepair()}
              disabled={busy}
            >
              {busy ? "Issuing…" : "Issue rebooking credit"}
            </button>
          </div>
        </Dialog>
      )}

      {clearing && (
        <Dialog title="Clear this payment reversal hold?" description="WMF will read the original Amelia payment again. Credits will only become spendable if it is confirmed paid and all block checks pass." onClose={() => { if (!busy) setClearing(null); }} size="small" busy={busy}>
          <label>Why the original payment is now valid
            <textarea value={clearReason} onChange={(event) => setClearReason(event.target.value)} rows={3} minLength={3} maxLength={2000} disabled={busy} />
          </label>
          {actionError && <p role="alert" className="error">{actionError}</p>}
          <div className="ui-confirm-actions">
            <button type="button" className="action-secondary" disabled={busy} onClick={() => setClearing(null)}>Cancel</button>
            <button type="button" className="action-primary" disabled={busy || clearReason.trim().length < 3} onClick={() => void confirmClear()}>{busy ? "Verifying…" : "Verify and clear hold"}</button>
          </div>
        </Dialog>
      )}

      {dismissing && (
        <Dialog
          title="Dismiss this credit review?"
          description="This records a deliberate human decision. Add a note explaining why no further action is needed."
          onClose={closeDismiss}
          size="small"
          busy={busy}
        >
          <label>
            Note (optional)
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={4}
              autoFocus
              disabled={busy}
            />
          </label>
          {actionError && <div className="error">{actionError}</div>}
          <div className="ui-confirm-actions">
            <button
              type="button"
              className="action-secondary"
              onClick={closeDismiss}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="action-primary"
              onClick={() => void confirmDismiss()}
              disabled={busy}
            >
              {busy ? "Dismissing…" : dismissAttempt ? "Retry dismissal" : "Dismiss review"}
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}
